<script setup lang="ts">
/**
 * План работ — макеты 9-1…9-3, 9-8 и 9-9 на странице «09 · План».
 *
 * Главная страница трекера: стек задач в том порядке, в котором человек
 * собрался работать, верхняя — текущая. «Мои задачи» остались вторым пунктом —
 * там список того, что на нём висит; здесь то, что он решил делать.
 *
 * Порядок правят руками, а не сортировкой: сортировка отвечает на «что важнее
 * по формуле», план — на «что я делаю после этого». Второе формулой не
 * выводится, и правит его тот, кто будет это делать.
 *
 * На телефоне переносить строку пальцем нечем — там кнопки порядка (макет
 * 9-8), и стек занимает экран целиком: панель кандидатов в 390 пикселей не
 * встаёт.
 */
import { AppButton, useToast } from '@vinprop/design-system'
import { AlertTriangle, Layers, Sparkles } from 'lucide-vue-next'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import draggable from 'vuedraggable'

import { errorMessage } from '@/api/errors'
import { openPalette } from '@/components/palette'
import TrackerEmptyState from '@/components/TrackerEmptyState.vue'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import { useIsMobile } from '@/composables/useIsMobile'
import CreateIssuePanel from '@/features/issues/components/CreateIssuePanel.vue'
import PlanCandidates from '@/features/plan/components/PlanCandidates.vue'
import PlanRow from '@/features/plan/components/PlanRow.vue'
import PlanWidgets from '@/features/plan/components/PlanWidgets.vue'
import { usePlanStore } from '@/features/plan/store'
import { useProjectsStore } from '@/features/projects/store'
import { useAssistantStore } from '@/stores/assistant'
import { useNavigationStore } from '@/stores/navigation.store'

import type { StackItem } from '@/features/plan/api'

const { t } = useI18n()
const toast = useToast()
const plan = usePlanStore()
const assistant = useAssistantStore()
const projects = useProjectsStore()
const navigation = useNavigationStore()
const isMobile = useIsMobile()

const candidates = ref<InstanceType<typeof PlanCandidates> | null>(null)

onMounted(() => {
  void plan.load()
  void plan.loadStats()
})

// Отказ сервера показываем тостом, а не строкой на экране: план к этому моменту
// уже откачен на место, и человеку нужно знать почему, а не смотреть на баннер
// поверх работающего списка.
watch(
  () => plan.error,
  (failure) => {
    if (failure) toast.error(errorMessage(failure, t('plan.failed')))
  },
)

/** Список для vuedraggable: порядок в нём и есть порядок плана */
const rows = computed({
  get: () => plan.items,
  set: (next: StackItem[]) => {
    plan.items = next
  },
})

interface ChangeEvent {
  added?: { element: { key: string }; newIndex: number }
  moved?: { newIndex: number }
}

/**
 * Строку в списке уже переставил vuedraggable. Своя строка уезжает целым
 * порядком, чужая — карточка кандидата — кладётся перед тем, кто оказался
 * следующим: сервер сам посчитает ей позицию.
 */
const onChange = (event: ChangeEvent) => {
  if (event.added) {
    const next = plan.items[event.added.newIndex + 1]?.key

    void plan.push(event.added.element.key, { top: event.added.newIndex === 0, before: next })

    return
  }

  if (event.moved) void plan.applyOrder()
}

/** Кнопки порядка телефона: строка меняется местами с соседней */
const moveBy = (key: string, delta: number) => {
  const from = plan.items.findIndex((item) => item.key === key)

  if (from !== -1) void plan.moveTo(key, from + delta)
}

const suggestOrder = () => {
  assistant.open()
  void assistant.send(t('plan.suggest.prompt'))
}

/**
 * Проект для создания задачи: последний открытый в сайдбаре, иначе первый из
 * списка. На плане проекта в адресе нет, а панель создания без него не живёт —
 * и спрашивать проект отдельным шагом ради кнопки в шапке значит превращать
 * «Создать» в форму из двух экранов.
 */
const createProject = computed(
  () =>
    projects.items.find((project) => project.key === navigation.expandedKey) ??
    projects.items[0] ??
    null,
)

const isCreateOpen = ref(false)

const onCreated = () => {
  void plan.loadStats()
}
</script>

<template>
  <TrackerToolbar :breadcrumbs="[{ label: t('nav.plan') }]">
    <AppButton variant="secondary" size="small" @click="suggestOrder">
      <Sparkles :size="14" />
      {{ t('plan.suggest.action') }}
    </AppButton>

    <!-- Поле поиска в шапке — та же палитра, что и ⌘K: второго поиска в
         трекере нет, и своё поле здесь означало бы второй способ искать. -->
    <button v-if="!isMobile" type="button" class="plan__search" @click="openPalette()">
      <span>{{ t('toolbar.search') }}</span>
      <kbd>⌘K</kbd>
    </button>

    <AppButton
      v-if="createProject"
      variant="primary"
      size="small"
      @click="isCreateOpen = true"
    >
      {{ t('toolbar.create') }}
    </AppButton>
  </TrackerToolbar>

  <div class="plan" :class="{ 'plan--mobile': isMobile }">
    <PlanWidgets :stats="plan.stats" :compact="isMobile" />

    <div class="plan__body">
      <section class="plan__stack">
        <header class="plan__head">
          <h2 class="plan__title">{{ t('plan.stack.title') }}</h2>
          <span class="plan__count">{{ plan.items.length }}</span>
          <span class="plan__note">{{ t('plan.stack.note') }}</span>
        </header>

        <div v-if="plan.isLoading && !plan.isLoaded" class="plan__skeleton">
          <span v-for="index in 5" :key="index" class="plan__skeleton-row" />
        </div>

        <TrackerEmptyState
          v-else-if="plan.error && !plan.isLoaded"
          :icon="AlertTriangle"
          :title="t('plan.failed')"
          :description="t('plan.failedHint')"
          tint="var(--color-danger-tint)"
        >
          <AppButton variant="secondary" @click="plan.load()">{{ t('plan.retry') }}</AppButton>
        </TrackerEmptyState>

        <!-- Пустой план — приглашение, а не ошибка (макет 9-2): рамка очерчивает
             ту самую область, куда несут карточку из поиска справа. -->
        <div v-else-if="!plan.items.length" class="plan__empty">
          <TrackerEmptyState
            :icon="Layers"
            :title="t('plan.empty.title')"
            :description="t('plan.empty.description')"
          >
            <AppButton
              v-if="!isMobile"
              variant="primary"
              @click="candidates?.focus()"
            >
              {{ t('plan.empty.action') }}
            </AppButton>
          </TrackerEmptyState>
        </div>

        <draggable
          v-else
          v-model="rows"
          class="plan__rows"
          item-key="issueId"
          :group="{ name: 'plan' }"
          :disabled="isMobile"
          handle=".prow__grip"
          :animation="180"
          :force-fallback="true"
          :fallback-on-body="true"
          :fallback-tolerance="4"
          ghost-class="prow--ghost"
          drag-class="prow--drag"
          @change="onChange"
          @start="plan.beginDrag()"
          @end="plan.endDrag()"
        >
          <template #item="{ element, index }">
            <PlanRow
              :item="element"
              :position="index + 1"
              :current="index === 0"
              :compact="isMobile"
              :first="index === 0"
              :last="index === plan.items.length - 1"
              @remove="plan.remove(element.key)"
              @up="moveBy(element.key, -1)"
              @down="moveBy(element.key, 1)"
            />
          </template>
        </draggable>
      </section>

      <aside v-if="!isMobile" class="plan__side">
        <PlanCandidates
          ref="candidates"
          :planned="plan.keys"
          @add="plan.push($event)"
        />
      </aside>
    </div>
  </div>

  <CreateIssuePanel
    v-if="createProject"
    v-model="isCreateOpen"
    :project-id="createProject.id"
    :type-profile="createProject.typeProfile"
    @created="onCreated"
  />
</template>

<style scoped>
.plan {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 20px;
  padding: 20px 24px;
}

.plan--mobile {
  gap: 12px;
  padding: 12px;
}

.plan__body {
  display: flex;
  flex: 1;
  gap: 20px;
  align-items: flex-start;
  min-height: 0;
}

.plan__stack {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 10px;
}

.plan__head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 2px;
}

.plan__title {
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  line-height: 22px;
}

.plan__count {
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
}

.plan__note {
  margin-left: auto;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  line-height: 16px;
}

.plan--mobile .plan__note {
  display: none;
}

.plan__rows {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Пустая область очерчена пунктиром: это и есть дроп-зона, и без рамки
   непонятно, куда именно нести карточку (макет 9-2). */
.plan__empty {
  display: flex;
  flex: 1;
  min-height: 320px;
  border: 1px dashed var(--color-border-strong);
  border-radius: var(--radius-md);
}

.plan__side {
  flex: 0 0 384px;
  width: 384px;
}

.plan__skeleton {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.plan__skeleton-row {
  height: 48px;
  border-radius: var(--radius-md);
  background: var(--color-bg-muted);
}

/* Летящая строка и её тень — язык переноса со страницы «06 · Состояния» */
.plan__rows :deep(.prow--ghost) {
  border-style: dashed;
  border-color: var(--color-primary);
  background: var(--color-primary-tint);
  opacity: 0.6;
}

.plan__rows :deep(.prow--drag) {
  box-shadow: var(--shadow-lg);
}

.plan__search {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text-muted);
  font-family: inherit;
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.plan__search kbd {
  padding: 1px 5px;
  border-radius: 4px;
  background: var(--color-bg-muted);
  font-family: inherit;
  font-size: var(--font-size-overline);
}
</style>
